import React, { useEffect, useMemo, useState } from 'react';
// @mui
import {
  Badge,
  Divider,
  IconButton,
  List,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { isWithinInterval, subSeconds } from 'date-fns';
// components
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
import MenuPopover from '../../../components/menu-popover';
import { IconButtonAnimate } from '../../../components/animate';
import { Job, JobStatus } from '../../../@types/jobs';
import { useGetAllFinishedJobsQuery, useMarkJobsAsReadMutation } from '../../../redux/slices/jobs';
import { useLocales } from '../../../locales';
import { dispatch } from '../../../redux/store';
import { apiSlice } from '../../../redux/slices/apiSlice';
import NotificationItem from './NotificationItem';

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const { translate } = useLocales();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);
  const [isPersonalized, setIsPersonalized] = useState(true);
  const [isUnread, setIsUnread] = useState(true);
  const [markJobsAsRead] = useMarkJobsAsReadMutation();

  const { data, isSuccess, refetch } = useGetAllFinishedJobsQuery(
    { id: 1, isPersonalized, isUnread },
    {
      pollingInterval: 1000 * 30,
      refetchOnMountOrArgChange: true, // once per 30 sec, if modified then invalidateTags function should also be adapted
    }
  );

  const unreadCount = useMemo(
    () => jobs.filter((item) => !item.read && item.status !== JobStatus.STARTED).length,
    [jobs]
  );

  const handleSwitchChangePersonalized = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsPersonalized(event.target.checked);
  };

  const handleSwitchChangeUnread = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsUnread(event.target.checked);
  };

  useEffect(() => {
    if (isSuccess && data) {
      invalidateTags(data);
      setJobs(data);
    }
  }, [data, isSuccess]);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  // To invalidate RTK Query tags after job is completed
  const invalidateTags = (result: Job[]) => {
    const now = new Date();
    const latestJobSinceInLastFetch = result.filter(
      (item) =>
        isWithinInterval(item.finished, { start: subSeconds(now, 30), end: now }) &&
        item.status === JobStatus.FINISHED &&
        !item.read
    );
    // Since tour planning and tour transform is async task we need to invalidate the tags after job is finished
    // Check if latest jobs consists TOUR_PLANNING_STUDENTS then invalidate TourPlan tag
    if (latestJobSinceInLastFetch.some((item) => item.type === 'TOUR_PLANNING_STUDENTS')) {
      dispatch(apiSlice.util.invalidateTags(['TourPlan']));
      dispatch(apiSlice.util.invalidateTags(['Tour']));
    }
    // Check if latest jobs consists TRANSFORM_PLANNED_TOURS then invalidate Tour tag
    if (latestJobSinceInLastFetch.some((item) => item.type === 'TRANSFORM_PLANNED_TOURS')) {
      dispatch(apiSlice.util.invalidateTags(['Tour']));
    }
    // Check if latest jobs consists REROUTE_TOURS then invalidate RoadblockTour tag
    if (latestJobSinceInLastFetch.some((item) => item.type === 'REROUTE_TOURS')) {
      dispatch(apiSlice.util.invalidateTags(['RoadblockTour']));
    }
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleMarkAllAsRead = async () => {
    await markJobsAsRead({ jobIds: jobs.map((jobStatus) => jobStatus.id) }).unwrap();
    setJobs([]);
  };

  const handleRefresh = async () => {
    refetch();
  };

  return (
    <>
      <IconButtonAnimate
        color={openPopover ? 'primary' : 'default'}
        onClick={handleOpenPopover}
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.25,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <Iconify icon="eva:bell-fill" />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover open={openPopover} onClose={handleClosePopover} sx={{ width: 420, p: 0 }}>
        <Stack sx={{ py: 2 }}>
          <Stack direction="row" gap={1} sx={{ px: 2.5 }}>
            <Stack>
              <Typography variant="subtitle1">{`${translate('jobs.notfications')}`}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {`${unreadCount} ${translate('jobs.unread_messages')}`}
              </Typography>
            </Stack>
            <Stack direction="row" marginLeft="auto" gap={0.5} padding={0}>
              {unreadCount > 0 && isPersonalized && isUnread && (
                <Tooltip title={`${translate('jobs.mark_all_read')}`}>
                  <IconButton color="primary" onClick={handleMarkAllAsRead}>
                    <Iconify icon="eva:done-all-fill" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={`${translate('jobs.refresh')}`}>
                <IconButton color="primary" onClick={handleRefresh}>
                  <Iconify icon="mage:reload" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" gap={1} alignItems="center" sx={{ px: 2.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
              {`${translate('jobs.personalizedNotifications')}`}
            </Typography>
            <Switch
              sx={{ marginLeft: 'auto' }}
              checked={isPersonalized}
              onChange={handleSwitchChangePersonalized}
              color="primary"
            />
          </Stack>
          <Stack direction="row" gap={1} alignItems="center" sx={{ px: 2.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
              {`${translate('jobs.unreadNotifications')}`}
            </Typography>
            <Switch
              sx={{ marginLeft: 'auto' }}
              checked={isUnread}
              onChange={handleSwitchChangeUnread}
              color="primary"
              disabled={!isPersonalized}
            />
          </Stack>
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Scrollbar
          sx={{
            height: {
              xs: 340,
              sm: 500,
              '.simplebar-content-wrapper': {
                borderBottomLeftRadius: '12px',
                borderBottomRightRadius: '12px',
              },
            },
          }}
        >
          <List disablePadding>
            {jobs.map((job) => (
              <NotificationItem key={job.id} notification={job} />
            ))}
          </List>
        </Scrollbar>
      </MenuPopover>
    </>
  );
}
